<template>
    <div>
        <DCard padding>
            <!--
                One alert for every state, colour carries the meaning: info while a run is under
                way, success once ours has finished, danger only when one could not be started.
                The link goes to the executions list rather than to one run, so it is offered
                from the moment a run starts and regardless of whose run it is.
            -->
            <DAlert
                v-if="invoicingMessage !== null"
                :color="invoicingColor"
                dismissible
                @close="invoicingMessage = null"
            >
                {{ invoicingMessage }}
                <Link
                    v-if="canViewExecutions"
                    :href="executionLink"
                >
                    {{ __('tools.partners.uploads.invoicing.check_execution') }}
                </Link>
            </DAlert>
            <DRow>
                <DCol>
                    <DTable
                        :items="uploads.items"
                        :fields="fields"
                    >
                        <template #actions="{item}">
                            <td>
                                <div style="display: flex; grid-gap: 10px;">
                                    <DButton
                                        icon="download"
                                        mini
                                        @click="downloadFile(item.id)"
                                    />
                                </div>
                            </td>
                        </template>
                        <template #date_of_upload="{item}">
                            <td>
                                {{ setDateFormat(item.date_of_upload) }}
                            </td>
                        </template>
                    </DTable>
                    <DPagination
                        v-if="uploads.last_page > 1"
                        :active-page="uploads.current_page"
                        :pages="uploads.last_page"
                        align="center"
                        @update:activePage="changePage"
                    />
                </DCol>
            </DRow>
            <DFabs>
                <!-- Two variants because v-c-tooltip captures its content on mount and never updates it. -->
                <DFab
                    v-if="canCreateInvoices && isInvoicingRunning"
                    v-c-tooltip="__('tools.partners.uploads.invoicing.running')"
                    icon="invoice-text-plus"
                    :label="__('tools.partners.uploads.invoicing.button')"
                    color="secondary"
                    disabled
                />
                <DFab
                    v-else-if="canCreateInvoices"
                    icon="invoice-text-plus"
                    :label="__('tools.partners.uploads.invoicing.button')"
                    color="secondary"
                    @click="showInvoicingModal = true"
                />
                <DFab
                    icon="plus"
                    :label="__('tools.partners.uploads.upload_new_partner_contracts')"
                    @click="showUploadModal = true"
                />
            </DFabs>
            <UploadFileModal
                :show-modal="showUploadModal"
                @close-modal="showUploadModal = false"
            />
            <CreateInvoicesModal
                :show="showInvoicingModal"
                @close="showInvoicingModal = false"
                @started="handleInvoicingStarted"
            />
        </DCard>
    </div>
</template>

<script>
import UploadFileModal from '~@/Components/Tools/Partners/Uploads/UploadFileModal'
import CreateInvoicesModal from '~@/Components/Tools/Partners/Uploads/CreateInvoicesModal'

const INVOICING_JOB = 'accounting:invoices:create'
// Matches CreatePartnerUploadInvoicesController::REASON_ALREADY_RUNNING.
const REASON_ALREADY_RUNNING = 'already_running'

export default {
    name: 'UploadIndex',
    components: { UploadFileModal, CreateInvoicesModal },
    props: {
        uploads: {
            type: Object,
            default: () => { return {} }
        },
        invoicingRunning: {
            type: Boolean,
            default: false
        },
        errors: {
            type: Object,
            default: () => { return {} }
        }
    },
    data () {
        return {
            fields: [
                { key: 'id', label: this.__('tools.partners.uploads.table.id') },
                { key: 'user', label: this.__('tools.partners.uploads.table.user') },
                { key: 'date_of_upload', label: this.__('tools.partners.uploads.table.date_of_upload') },
                { key: 'number_of_records', label: this.__('tools.partners.uploads.table.number_of_records') },
                { key: 'filename', label: this.__('tools.partners.uploads.table.filename') },
                { key: 'actions', label: '' }
            ],
            showUploadModal: false,
            showInvoicingModal: false,
            invoicingMessage: null,
            invoicingColor: 'info',
            // Only set for a run started from this page, which is what tells "ours" from a run
            // that was already going, and so whether the outcome is worth reporting.
            invoicingPid: null,
            // Seeded from the server-rendered prop, then kept current by the websocket event below
            // so the styling clears when the run finishes without needing a reload.
            isInvoicingRunning: this.invoicingRunning
        }
    },
    computed: {
        // The endpoint requires invoice.create on top of contract.partners, so someone holding
        // only the partner permission is not offered a button that would 403.
        canCreateInvoices () {
            return (this.$page.props.auth?.permissions ?? []).includes('invoice.create')
        },
        // The executions pages sit behind their own permission, so an agent who only holds
        // contract.partners gets the success message without a link they cannot follow.
        canViewExecutions () {
            return (this.$page.props.auth?.permissions ?? []).includes('admin.cron_jobs_executions')
        },
        // The executions list, filtered to this job. It is ordered newest first, so the run just
        // started is the top row - which is as close to it as we can link: the per-execution
        // detail page was removed when cron job output moved to S3, and the list cannot be
        // filtered by process id.
        executionLink () {
            return this.$route('administrations.cron-jobs.executions', {
                job: INVOICING_JOB
            })
        }
    },
    created () {
        // Arrived with a run under way. Whether it is ours is unknowable after a reload, and
        // "already in progress" is true either way.
        if (this.isInvoicingRunning === true) {
            this.showAlreadyRunning()
        }
    },
    mounted () {
        window.laravelEcho.channel('onecrm.cron_job_executions.updated')
            .listen('.cron_job_executions.status_updated', socketEvent => this.handleExecutionUpdate(socketEvent))
    },
    beforeUnmount () {
        // leaveChannel, not leave: leave() would also tear down the private- and presence-
        // variants of the same name, which this component never subscribed to.
        window.laravelEcho.leaveChannel('onecrm.cron_job_executions.updated')
    },
    methods: {
        downloadFile (id) {
            const link = document.createElement('a')
            link.href = this.$route('tools.partners.download', { partner_upload: id })
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        changePage (page) {
            const queryParameters = {}

            if (page > 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('tools.partners.partner-uploads.index'), queryParameters)
        },
        handleInvoicingStarted (response) {
            // Started here: keep the process id, which is what marks the run as ours.
            if (response.success === true) {
                this.invoicingPid = response.pid ?? null
                this.isInvoicingRunning = true
                this.showRunning()

                return
            }

            // Refused because something is already running. No process id, so no link - the run
            // could be the scheduled one, or one started in another tab.
            if (response.reason === REASON_ALREADY_RUNNING) {
                this.invoicingPid = null
                this.isInvoicingRunning = true
                this.showAlreadyRunning()

                return
            }

            // Could not be started at all - the only genuine error state.
            this.invoicingPid = null
            this.invoicingColor = 'danger'
            this.invoicingMessage = response.message
        },
        // A run we started ourselves - nothing to wait for that we did not ask for.
        showRunning () {
            this.invoicingColor = 'info'
            this.invoicingMessage = this.__('tools.partners.uploads.invoicing.running')
        },
        // A run that was already going when we got here, or that blocked our click.
        showAlreadyRunning () {
            this.invoicingColor = 'info'
            this.invoicingMessage = this.__('tools.partners.uploads.invoicing.already_running')
        },
        handleExecutionUpdate (socketEvent) {
            if (socketEvent.tenant !== this.$page.props.tenant) {
                return
            }

            const execution = socketEvent.cronJobExecution

            if (execution?.job !== INVOICING_JOB) {
                return
            }

            const wasRunning = this.isInvoicingRunning === true

            this.isInvoicingRunning = execution.status === 'running'

            if (this.isInvoicingRunning === true || wasRunning === false) {
                return
            }

            // A run we only observed has ended: drop the alert and let the button speak for
            // itself. There is no outcome to report, because it was not started from here.
            if (this.invoicingPid === null) {
                this.invoicingMessage = null

                return
            }

            // Our run ended - report how it went, keeping the link to it.
            this.invoicingColor = execution.status === 'done' ? 'success' : 'danger'
            this.invoicingMessage = execution.status === 'done'
                ? this.__('tools.partners.uploads.invoicing.finished')
                : this.__('tools.partners.uploads.invoicing.run_failed')
        }
    }
}
</script>

<style scoped>

</style>
